import React, { useEffect, useMemo, useRef, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api, API_BASE } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, Download } from "lucide-react";
import { localizeError } from "@/lib/errors";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const empty = { name_en: "", name_ar: "", description_en: "", description_ar: "", price: 0, duration_min: 60, package_type: "fixed", pick_count: 3, service_ids: [], product_items: [], active: true, show_on_website: true, is_featured: false, image_url: "" };
const PAGE_SIZE = 50;

export default function Packages() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const fileRef = useRef(null);

  // Excel template download — backend builds a styled workbook with sample row.
  const downloadTemplate = async () => {
    try {
      const r = await fetch(`${API_BASE}/packages/template`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "packages_template.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error(lang === "ar" ? "تعذر تنزيل القالب" : "Failed to download template"); }
  };

  const exportExcel = async () => {
    try {
      const r = await fetch(`${API_BASE}/packages/export`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `packages_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(lang === "ar" ? `تم تصدير ${items.length} باقة` : `Exported ${items.length} packages`);
    } catch { toast.error(lang === "ar" ? "فشل التصدير" : "Export failed"); }
  };

  const onFile = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/packages/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.created} ${t("imported_count")}, ${r.data.skipped} ${t("skipped_count")}`);
      ev.target.value = "";
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "ar" ? "فشل الاستيراد" : "Import failed"));
    }
  };

  const load = async () => {
    const [p, s, pr] = await Promise.all([api.get("/packages"), api.get("/services"), api.get("/products")]);
    setItems(p.data); setServices(s.data); setProducts(pr.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [search]);

  const submit = async () => {
    try {
      const pickCount = parseInt(form.pick_count, 10) || 0;
      if (form.package_type === "pick_n" && pickCount < 1) {
        toast.error(lang === "ar" ? "حدد عدد الخدمات المطلوب اختيارها" : "Set how many services must be chosen");
        return;
      }
      if (form.package_type === "pick_n" && (form.service_ids || []).length < pickCount) {
        toast.error(lang === "ar" ? "يجب أن تكون الخدمات المؤهلة أكثر من أو تساوي العدد المطلوب" : "Eligible services must be at least the pick count");
        return;
      }
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        duration_min: parseInt(form.duration_min) || 60,
        package_type: form.package_type || "fixed",
        pick_count: form.package_type === "pick_n" ? pickCount : 0,
        product_items: (form.product_items || []).filter(pi => pi.product_id && parseInt(pi.qty) > 0).map(pi => ({ product_id: pi.product_id, qty: parseInt(pi.qty) })),
      };
      if (editId) await api.put(`/packages/${editId}`, payload);
      else await api.post("/packages", payload);
      toast.success(t("saved")); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: lang === "ar" ? "حذف الباقة؟" : "Delete package?",
      confirmLabel: t("delete"),
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/packages/${id}`); load();
  };

  const toggleSvc = (sid) => {
    setForm(f => ({ ...f, service_ids: f.service_ids.includes(sid) ? f.service_ids.filter(x => x !== sid) : [...f.service_ids, sid] }));
  };

  // Wave D — package products manipulation
  const addProductLine = () => setForm(f => ({ ...f, product_items: [...(f.product_items || []), { product_id: "", qty: 1 }] }));
  const updateProductLine = (idx, key, val) => setForm(f => {
    const next = [...(f.product_items || [])];
    next[idx] = { ...next[idx], [key]: val };
    return { ...f, product_items: next };
  });
  const removeProductLine = (idx) => setForm(f => ({ ...f, product_items: (f.product_items || []).filter((_, i) => i !== idx) }));

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      (p.name_en || "").toLowerCase().includes(q) ||
      (p.name_ar || "").toLowerCase().includes(q) ||
      (p.description_en || "").toLowerCase().includes(q) ||
      (p.description_ar || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredItems, page],
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div className="space-y-6" data-testid="packages-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-4xl gold-text">{t("nav_packages")}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={downloadTemplate} data-testid="packages-template-btn" className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Download size={14}/>{t("download_template")}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onFile} className="hidden" data-testid="packages-import-file"/>
          <button onClick={() => fileRef.current?.click()} data-testid="packages-import-btn" className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Upload size={14}/>{t("import_excel")}
          </button>
          <button onClick={exportExcel} data-testid="packages-export-btn" className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Download size={14}/>{lang === "ar" ? "تصدير" : "Export"}
          </button>
          <button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-package-btn" className="btn-gold px-4 py-2 rounded-md flex items-center gap-2"><Plus size={16}/>{t("add")}</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <input
          className="input-dark max-w-sm"
          placeholder={lang === "ar" ? "ابحث بالاسم أو الوصف..." : "Search by name or description..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="packages-search"
        />
        {filteredItems.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm text-[var(--text-muted)]">
              {lang === "ar"
                ? `عرض ${Math.min((page - 1) * PAGE_SIZE + 1, filteredItems.length)}-${Math.min(page * PAGE_SIZE, filteredItems.length)} من ${filteredItems.length}`
                : `Showing ${Math.min((page - 1) * PAGE_SIZE + 1, filteredItems.length)}-${Math.min(page * PAGE_SIZE, filteredItems.length)} of ${filteredItems.length}`}
            </div>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-rose-outline px-3 py-2 rounded-md text-sm disabled:opacity-50">{lang === "ar" ? "السابق" : "Prev"}</button>
            <span className="text-sm text-[var(--text-muted)]">{page}/{totalPages}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-rose-outline px-3 py-2 rounded-md text-sm disabled:opacity-50">{lang === "ar" ? "التالي" : "Next"}</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {pageItems.map(p => (
          <div key={p.package_id} className="card-surface p-5 flex flex-col">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-heading text-xl text-[var(--text)]">{lang === "ar" ? p.name_ar : p.name_en}</div>
                <div className="text-xs text-[var(--text-muted)] mt-1">
                  {p.package_type === "pick_n"
                    ? (lang === "ar" ? `اختر ${p.pick_count || 0} من ${p.service_ids?.length || 0} خدمة` : `Pick ${p.pick_count || 0} of ${p.service_ids?.length || 0} services`)
                    : `${p.service_ids?.length || 0} ${t("services_count")}`}
                  {(p.product_items?.length || 0) > 0 && (
                    <span className="ms-2">· {p.product_items.length} {lang === "ar" ? "منتجات" : "products"}</span>
                  )}
                </div>
              </div>
              <div className="gold-text font-heading text-2xl">{p.price}</div>
            </div>
            <p className="text-sm text-[var(--text-muted)] flex-1">{lang === "ar" ? p.description_ar : p.description_en}</p>
            <div className="flex flex-wrap gap-1 my-3">
              {p.service_ids?.map(sid => {
                const svc = services.find(s => s.service_id === sid);
                return svc ? <span key={sid} className="badge badge-gold">{lang === "ar" ? svc.name_ar : svc.name_en}</span> : null;
              })}
            </div>
            <div className="flex gap-2 mt-auto">
              <button onClick={() => { setForm({ ...empty, ...p, package_type: p.package_type || "fixed", pick_count: p.pick_count || 0 }); setEditId(p.package_id); setOpen(true); }} className="flex-1 btn-rose-outline py-2 rounded-md text-sm flex items-center justify-center gap-2"><Pencil size={14}/>{t("edit")}</button>
              <button onClick={() => remove(p.package_id)} className="p-2 rounded border border-[var(--border)] hover:border-red-500"><Trash2 size={16}/></button>
            </div>
          </div>
        ))}
        {pageItems.length === 0 && <div className="text-[var(--text-muted)] col-span-full text-center py-12">{search ? (lang === "ar" ? "لا نتائج تطابق البحث" : "No results match your search") : t("no_data")}</div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="card-surface p-6 w-full max-w-2xl space-y-4 max-h-[90vh] overflow-auto" onMouseDown={onModalPanelMouseDown}>
            <h2 className="font-heading text-2xl gold-text">{editId ? t("edit") : t("add")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-xs text-[var(--text-muted)]">{t("name_en")}</label><input className="input-dark" value={form.name_en} onChange={e => setForm({...form, name_en: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("name_ar")}</label><input className="input-dark" value={form.name_ar} onChange={e => setForm({...form, name_ar: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("price")}</label><input className="input-dark" type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("duration")}</label><input className="input-dark" type="number" value={form.duration_min} onChange={e => setForm({...form, duration_min: e.target.value})}/></div>
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)]">{t("description_en")}</label><textarea className="input-dark" rows={2} value={form.description_en} onChange={e => setForm({...form, description_en: e.target.value})}/></div>
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)]">{t("description_ar")}</label><textarea className="input-dark" rows={2} value={form.description_ar} onChange={e => setForm({...form, description_ar: e.target.value})}/></div>
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "رابط صورة الباقة" : "Package Image URL"}</label><input className="input-dark" placeholder="https://..." value={form.image_url || ""} onChange={e => setForm({...form, image_url: e.target.value})}/></div>
              <div className="flex items-center gap-2 pt-2"><input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})}/><span className="text-sm text-[var(--text)]">{t("active")}</span></div>
              <div className="flex items-center gap-2 pt-2"><input type="checkbox" data-testid="package-show-on-website" checked={form.show_on_website !== false} onChange={e => setForm({...form, show_on_website: e.target.checked})}/><span className="text-sm text-[var(--text)]">{lang === "ar" ? "إظهار على الموقع" : "Show on Website"}</span></div>
              <div className="flex items-center gap-2 pt-2"><input type="checkbox" data-testid="package-is-featured" checked={!!form.is_featured} onChange={e => setForm({...form, is_featured: e.target.checked})}/><span className="text-sm text-[var(--text)]">{lang === "ar" ? "باقة مميزة" : "Featured"}</span></div>
              <div className="md:col-span-2">
                <label className="text-xs text-[var(--text-muted)] block mb-2">{lang === "ar" ? "نوع الباقة" : "Package type"}</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { v: "fixed", l: lang === "ar" ? "ثابتة (خدمات محددة)" : "Fixed (predefined services)" },
                    { v: "pick_n", l: lang === "ar" ? "اختيار من قائمة (مثال: أي 3)" : "Pick N (e.g. any 3)" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`package-type-${opt.v}`}
                      onClick={() => setForm({ ...form, package_type: opt.v, pick_count: opt.v === "pick_n" ? (form.pick_count || 3) : 0 })}
                      className={`px-3 py-1.5 rounded-md text-xs border ${form.package_type === opt.v ? "btn-gold" : "border-[var(--border)] hover:border-[var(--gold)]"}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>
              {form.package_type === "pick_n" && (
                <div>
                  <label className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "عدد الخدمات المطلوب اختيارها" : "Services to pick"}</label>
                  <input className="input-dark" type="number" min={1} value={form.pick_count || 1} onChange={e => setForm({ ...form, pick_count: e.target.value })} data-testid="package-pick-count" />
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-[var(--text-muted)]">
                {form.package_type === "pick_n"
                  ? (lang === "ar" ? "الخدمات المؤهلة (العميل يختار منها)" : "Eligible services (customer picks from these)")
                  : t("nav_services")}
              </label>
              <div className="flex flex-wrap gap-2 mt-2">
                {services.map(s => (
                  <button key={s.service_id} type="button" onClick={() => toggleSvc(s.service_id)}
                    className={`badge ${form.service_ids.includes(s.service_id) ? "badge-gold" : ""} cursor-pointer`}>
                    {lang === "ar" ? s.name_ar : s.name_en}
                  </button>
                ))}
              </div>
            </div>

            {/* Wave D — Products inside package */}
            <div data-testid="package-products-section">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "منتجات داخل الباقة (يُخصم من المخزون عند البيع)" : "Products in package (deducted from stock on sale)"}</label>
                <button type="button" onClick={addProductLine} data-testid="add-pkg-product"
                  className="btn-soft px-2 py-1 rounded text-xs flex items-center gap-1">
                  <Plus size={12}/>{lang === "ar" ? "إضافة منتج" : "Add product"}
                </button>
              </div>
              <div className="space-y-2">
                {(form.product_items || []).map((pi, idx) => (
                  <div key={idx} className="flex gap-2 items-center" data-testid={`pkg-product-row-${idx}`}>
                    <select className="input-dark flex-1" value={pi.product_id}
                      onChange={e => updateProductLine(idx, "product_id", e.target.value)}>
                      <option value="">{lang === "ar" ? "اختر منتج" : "Select product"}</option>
                      {products.map(p => (
                        <option key={p.product_id} value={p.product_id}>
                          {lang === "ar" ? p.name_ar : p.name_en} {p.stock != null ? `(${lang === "ar" ? "المخزون" : "stock"}: ${p.stock})` : ""}
                        </option>
                      ))}
                    </select>
                    <input type="number" min="1" className="input-dark num-display w-24" value={pi.qty}
                      onChange={e => updateProductLine(idx, "qty", e.target.value)} placeholder="Qty"/>
                    <button type="button" onClick={() => removeProductLine(idx)}
                      className="p-2 rounded border border-[var(--border)] hover:border-red-500"><Trash2 size={14}/></button>
                  </div>
                ))}
                {(form.product_items || []).length === 0 && (
                  <div className="text-xs text-[var(--text-muted)] italic">{lang === "ar" ? "(اختياري) — اتركه فارغاً إذا الباقة خدمات فقط" : "(Optional) — leave empty for service-only packages"}</div>
                )}
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)] text-[var(--text)]" onClick={() => setOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" data-testid="package-save-btn" onClick={submit}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
